type MetricFormatKind =
  | 'days'
  | 'currency-abbrev'
  | 'currency'
  | 'rate'
  | 'units'
  | 'orders'
  | 'customers'
  | 'number';

const METRIC_FORMAT: Record<string, MetricFormatKind> = {
  'avg-resolution-time': 'days',
  'avg-days-sale-to-cyd': 'days',
  'total-sales': 'currency-abbrev',
  'total-cyd-amount': 'currency-abbrev',
  'total-shipping-cost': 'currency',
  'average-shipping-cost': 'currency',
  'average-shipping-cost-sucursal': 'currency',
  'average-shipping-cost-domicilio': 'currency',
  'total-shipping-quoted': 'currency',
  'total-shipping-collected': 'currency',
  'shipping-collection-gap': 'currency',
  'shipping-quoted-collection-gap': 'currency',
  'cyd-rate': 'rate',
  'cyd-rate-amount': 'rate',
  'retention-rate': 'rate',
  'products-sold': 'units',
  'total-cyd-units': 'units',
  'total-orders': 'orders',
  'orders-with-cyd': 'orders',
  'open-orders': 'orders',
  'total-customers': 'customers',
  'new-customers': 'customers',
  'recurring-customers': 'customers',
};

function getFormatKind(metricId: string): MetricFormatKind {
  if (metricId in METRIC_FORMAT) {
    return METRIC_FORMAT[metricId];
  }
  if (metricId.includes('cyd') && metricId.includes('rate')) {
    return 'rate';
  }
  return 'number';
}

function formatAbbrevCurrency(amount: number, decimals = 1): string {
  const abs = Math.abs(amount);
  const sign = amount < 0 ? '-' : '';
  if (abs >= 1_000_000) {
    const scaled = (abs / 1_000_000).toFixed(decimals).replace(/\.0$/, '').replace('.', ',');
    return `${sign}$${scaled} M`;
  }
  if (abs >= 1_000) {
    const scaled = (abs / 1_000).toFixed(decimals).replace(/\.0$/, '').replace('.', ',');
    return `${sign}$${scaled} K`;
  }
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatRate(value: number): string {
  const formatted = value.toFixed(2).replace(/\.00$/, '').replace('.', ',');
  return `${formatted}%`;
}

function formatCount(value: number, suffix: string): string {
  const formatted = new Intl.NumberFormat('es-AR', {
    maximumFractionDigits: value % 1 === 0 ? 0 : 1,
  }).format(value);
  return `${formatted}${suffix}`;
}

/** Main stat-card value (large number). */
export function formatMetricValue(metricId: string, value: number): string {
  const kind = getFormatKind(metricId);

  switch (kind) {
    case 'days':
      return `${new Intl.NumberFormat('es-AR', { maximumFractionDigits: 1 }).format(value)} días`;
    case 'currency-abbrev':
      return formatAbbrevCurrency(value);
    case 'currency':
      return new Intl.NumberFormat('es-AR', {
        style: 'currency',
        currency: 'ARS',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
    case 'rate':
      return formatRate(value);
    case 'units':
      return formatCount(value, ' unid.');
    case 'orders':
      return formatCount(value, ' órdenes');
    case 'customers':
      return formatCount(value, ' clientes');
    default:
      return new Intl.NumberFormat('es-AR').format(value);
  }
}

/** Previous-period reference in footnote (vs. … · N días anteriores). */
export function formatComparisonValue(metricId: string, value: number): string {
  if (value === 0) {
    return '0';
  }
  return formatMetricValue(metricId, value);
}

export function isNegligibleComparisonBase(metricId: string, previous: number): boolean {
  if (previous === 0) {
    return true;
  }

  const kind = getFormatKind(metricId);
  const abs = Math.abs(previous);

  switch (kind) {
    case 'rate':
      return abs < 0.1;
    case 'currency-abbrev':
    case 'currency':
      return abs < 1;
    case 'days':
      return abs < 0.05;
    default:
      return abs < 1;
  }
}

function formatPercentChange(change: number): string {
  const abs = Math.abs(change);
  const formatted =
    abs >= 100
      ? Math.round(abs).toString()
      : abs.toFixed(1).replace(/\.0$/, '').replace('.', ',');
  const sign = change > 0 ? '+' : '-';
  const arrow = change > 0 ? '↑' : '↓';
  return `${sign}${formatted}% ${arrow}`;
}

/** Minimum |change| to show chip (below ≈ stable). */
const MIN_PERCENT_FOR_CHIP = 5;

export function formatPercentChip(current: number, previous: number): string | null {
  if (current === previous) {
    return null;
  }
  if (previous <= 0) {
    return null;
  }

  const change = ((current - previous) / previous) * 100;
  if (!Number.isFinite(change)) {
    return null;
  }

  if (Math.abs(change) < MIN_PERCENT_FOR_CHIP) {
    return null;
  }

  return formatPercentChange(change);
}
